import { useMemo } from "react";

import {
    LendStats,
    useLenderStrategies,
    StrategyExpanded,
    useLoopPositionStatsQuery,
    calculateTotalInterestAccrued,
    BorrowStats,
    PortfolioStats,
    usePortfolioStatsQuery,
    useLendVaultPositionStatsQuery,
    LendVaultStatsResponse,
    useOraclePrices,
    useTokenListMetadataByMints,
    StrategyRewards,
    useActivePortfolioRewards,
    usePortfolioRewards,
    useUserLendingVaultPositions,
    LedgerReward,
    VaultReward,
    LoopscalePointsAction
} from "@bridgesplit/abf-react";
import { bpsToUiDecimals, lamportsToUiAmount, percentUiToDecimals, TIME, TIME_IN_DAYS } from "@bridgesplit/utils";
import { useLendingPositionPagination } from "app/api";

import {
    BorrowStatsExpanded,
    LendingVaultStats,
    LendStatsExpanded,
    OverviewStats,
    SummarizedActivePortfolioRewards,
    SummarizedStats
} from "./type";

export function usePortfolioStats() {
    const { data: portfolioStats } = usePortfolioStatsQuery();
    const { data: loopPositionStats } = useLoopPositionStatsQuery();
    const { data: strategies } = useLenderStrategies({});
    const { data: lendVaultsPositionStats } = useLendVaultPositionStatsQuery();

    const lendVaultStats = useCalculateLendVaultStats(lendVaultsPositionStats);

    const stats = useMemo((): SummarizedStats | undefined => {
        if (!portfolioStats || !strategies || !loopPositionStats || !lendVaultsPositionStats) return undefined;

        const lendStats = calculateLendStats(strategies);
        const borrowStats = calculateBorrowStats(portfolioStats);
        const loopStats = loopPositionStats;
        const overviewStats = summarizeStats({
            lendStats,
            borrowStats,
            loopStats,
            lendVaultStats
        });
        return {
            lendStats,
            borrowStats,
            loopStats,
            lendVaultStats,
            overviewStats
        };
    }, [portfolioStats, strategies, loopPositionStats, lendVaultsPositionStats, lendVaultStats]);

    return stats;
}
function calculateLendStats(strategies: StrategyExpanded[]): LendStatsExpanded {
    const strategyBalanceUsd = strategies.reduce(
        (acc, strategy) => acc + strategy.totalBalance * (strategy.principalUsdPrice ?? 0),
        0
    );
    let summary: LendStats = {
        principalDeployedUsd: 0,
        wAvgApy: 0,
        interestAccruedUsd: 0,
        interestEarnedAllTimeUsd: 0,
        expected24hInterestUsd: 0
    };
    let totalWeight = 0;
    for (const strategy of strategies) {
        const usdPrice = strategy.principalUsdPrice ?? 0;

        // wAvgApy needs to include external yield + deployed principal * deployed apy
        const deployedUsd = strategy.strategy.currentDeployedAmount * usdPrice;
        let wAvgApy = strategy.strategy.fixedApy * deployedUsd;
        totalWeight += deployedUsd;

        // add weighted external yield
        if (strategy.externalYieldInfo) {
            const yieldDepositsUsd = strategy.externalYieldInfo.balance * usdPrice;
            totalWeight += yieldDepositsUsd;
            wAvgApy += strategy.externalYieldInfo.apy * yieldDepositsUsd;
        } else {
            // if no external yield, add the total balance as idle
            totalWeight += strategy.totalBalance * usdPrice;
        }

        const interestAccrued = calculateTotalInterestAccrued(strategy, {
            includeStrategyInterest: true,
            includeExternalInterest: true,
            includeCumulativeInterest: true
        });

        summary = {
            principalDeployedUsd: summary.principalDeployedUsd + strategy.strategy.currentDeployedAmount * usdPrice,
            interestAccruedUsd: summary.interestAccruedUsd + interestAccrued * usdPrice,
            interestEarnedAllTimeUsd: summary.interestEarnedAllTimeUsd + interestAccrued * usdPrice,
            wAvgApy: summary.wAvgApy + wAvgApy,
            expected24hInterestUsd: 0
        };
    }

    summary.wAvgApy = summary.wAvgApy / (totalWeight || 1);
    summary.expected24hInterestUsd = (summary.wAvgApy / TIME_IN_DAYS.YEAR) * (totalWeight || 1);

    const totalSupplyUsd = summary.principalDeployedUsd + strategyBalanceUsd;

    return { ...summary, strategyBalanceUsd, totalSupplyUsd };
}

function calculateBorrowStats(portfolioStats: PortfolioStats): BorrowStatsExpanded {
    let summary: BorrowStats = {
        principalBorrowedUsd: 0,
        interestAccruedUsd: 0,
        wAvgApy: 0,
        collateralUsd: 0
    };
    let totalWeight = 0;
    for (const stats of Object.values(portfolioStats.borrowStats)) {
        const weight = stats.principalBorrowedUsd;
        totalWeight += weight;

        summary = {
            principalBorrowedUsd: summary.principalBorrowedUsd + stats.principalBorrowedUsd,
            interestAccruedUsd: summary.interestAccruedUsd + stats.interestAccruedUsd,
            wAvgApy: summary.wAvgApy + bpsToUiDecimals(stats.wAvgApy) * weight,
            collateralUsd: summary.collateralUsd + stats.collateralUsd
        };
    }
    summary.wAvgApy = summary.wAvgApy / (totalWeight || 1);

    const totalBorrowedUsd = summary.principalBorrowedUsd + summary.interestAccruedUsd;
    const netPositionUsd = Math.max(summary.collateralUsd - totalBorrowedUsd, 0);

    return { ...summary, netPositionUsd, totalBorrowedUsd };
}

function summarizeStats({
    lendStats,
    borrowStats,
    loopStats,
    lendVaultStats
}: Omit<SummarizedStats, "overviewStats">): OverviewStats {
    const borrowNetValue =
        borrowStats.collateralUsd - borrowStats.principalBorrowedUsd - borrowStats.interestAccruedUsd;
    const lendNetValue = lendStats.strategyBalanceUsd + lendStats.principalDeployedUsd + lendStats.interestAccruedUsd;
    const loopNetValue = loopStats.netPositionValue;
    const lendVaultNetValue = lendVaultStats.totalValueUsd;

    const netValue = lendNetValue + borrowNetValue + loopNetValue + lendVaultNetValue;

    // Does not include interest earned from lend vaults since we do not show PnL
    const totalInterestEarned = lendStats.interestEarnedAllTimeUsd;
    return {
        netValue,
        totalInterestEarned
    };
}

// Takes in the response from the lend vaults stats route and returns the sumarized lending vault stat for all principals
function useCalculateLendVaultStats(stats: LendVaultStatsResponse | undefined): LendingVaultStats {
    const mints = stats ? Object.keys(stats) : [];
    const { getUsdPrice } = useOraclePrices(mints);
    const { getMetadata } = useTokenListMetadataByMints(mints);

    if (!stats) {
        return {
            wAvgApy: 0,
            totalValueUsd: 0,
            expected24hInterestUsd: 0
        };
    }

    let totalApy = 0;
    let totalValueUsd = 0;

    const elements = Object.entries(stats);
    for (const [mint, rawStat] of elements) {
        const convertedStat = {
            ...rawStat,
            totalValue: lamportsToUiAmount(rawStat.totalValue, getMetadata(mint)?.decimals)
        };
        const usdPrice = getUsdPrice(mint);

        const valueUsd = convertedStat.totalValue * (usdPrice ?? 0);

        totalApy += convertedStat.wAvgApy * valueUsd;
        totalValueUsd += valueUsd;
    }

    const wAvgApy = percentUiToDecimals(totalApy / (totalValueUsd || 1));
    const expected24hInterestUsd = (wAvgApy / TIME_IN_DAYS.YEAR) * (totalValueUsd || 1);

    return {
        wAvgApy,
        totalValueUsd,
        expected24hInterestUsd
    };
}

interface ActivePortfolioRewardsResponse {
    data: SummarizedActivePortfolioRewards | undefined;
    isLoading: boolean;
}
export function useActivePortfolioRewardsSummary(): ActivePortfolioRewardsResponse {
    const { data: portfolioRewards, isLoading: isLoadingPortfolioRewards } = useActivePortfolioRewards();

    // Use useMemo to ensure this calculation only runs when dependencies change
    const data = useMemo(() => {
        if (!portfolioRewards) return undefined;

        return {
            activeStrategyDailyLendPoints: summarizeDailyActiveLendPoints(portfolioRewards.strategies ?? {}),
            activeBorrowDailyPoints: summarizeDailyActiveBorrowPoints(portfolioRewards.loans ?? {}),
            activeLendVaultDailyPoints: summarizeDailyActiveLendVaultPoints(portfolioRewards.vaultStakes ?? {}),
            activeLoopDailyPoints: summarizeDailyActiveBorrowPoints(portfolioRewards.loops ?? {})
        };
    }, [portfolioRewards]);

    return {
        data,
        isLoading: isLoadingPortfolioRewards
    };
}

function summarizeDailyActiveLendPoints(strategies: Record<string, StrategyRewards>): number | undefined {
    let totalPoints = 0;
    let hasActiveStrategy = false;

    for (const strategy of Object.values(strategies)) {
        const points = calculateActiveStrategyDailyPoints(strategy);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveStrategy = true;
        }
    }

    return hasActiveStrategy ? totalPoints : undefined;
}

function summarizeDailyActiveBorrowPoints(loans: Record<string, LedgerReward[]>): number | undefined {
    let totalPoints = 0;
    let hasActiveLoan = false;

    for (const loan of Object.values(loans)) {
        const points = calculateActiveLoanDailyPoints(loan, true);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveLoan = true;
        }
    }

    return hasActiveLoan ? totalPoints : undefined;
}

function summarizeDailyActiveLendVaultPoints(vaultStakes: Record<string, VaultReward>): number | undefined {
    let totalPoints = 0;
    let hasActiveStake = false;

    for (const stake of Object.values(vaultStakes)) {
        const points = calculateLendVaultDailyPoints(stake);
        if (points !== undefined) {
            totalPoints += points;
            hasActiveStake = true;
        }
    }

    return hasActiveStake ? totalPoints : undefined;
}

export function getStrategyIdleCapMultiplier(strategyReward: StrategyRewards | undefined): number {
    return strategyReward?.externalYieldRewards?.baseReward?.multiplier ?? 0;
}

export function getStrategyLendMultiplier(strategyReward: StrategyRewards | undefined): number {
    if (!strategyReward?.ledgerRewards?.length) return 0;

    const activeLenderRewards = strategyReward.ledgerRewards.filter(
        (reward) => reward.baseReward.isLenderReward && reward.baseReward.isActive
    );

    const multiplierMax = Math.max(...activeLenderRewards.map((reward) => reward.baseReward.multiplier));

    return multiplierMax;
}

export function getStrategyLoopscaleActions(strategyReward: StrategyRewards | undefined): LoopscalePointsAction[] {
    if (!strategyReward) return [];

    const actions: LoopscalePointsAction[] = [];

    for (const reward of strategyReward.ledgerRewards ?? []) {
        actions.push(reward.baseReward.reward as LoopscalePointsAction);
    }

    actions.push(strategyReward.externalYieldRewards?.baseReward?.reward as LoopscalePointsAction);

    return actions;
}

export function calculateActiveStrategyDailyPoints(strategyReward: StrategyRewards | undefined): number | undefined {
    if (!strategyReward) return undefined;

    const ledgerPoints =
        strategyReward.ledgerRewards
            ?.filter((reward) => reward.baseReward.isLenderReward)
            .reduce((sum, reward) => {
                return sum + (reward.baseReward?.pointsPerSecond || 0);
            }, 0) || 0;

    const externalPoints = strategyReward.externalYieldRewards?.baseReward?.pointsPerSecond || 0;

    const totalPoints = ledgerPoints + externalPoints;
    const dailyPoints = totalPoints * TIME.DAY;

    return dailyPoints > 0 ? dailyPoints : undefined;
}

export function calculateActiveLoanDailyPoints(
    loanReward: LedgerReward[] | undefined,
    isBorrower: boolean
): number | undefined {
    if (!loanReward || !Array.isArray(loanReward)) return undefined;

    const ledgerPoints = loanReward.reduce((sum, reward) => {
        // Skip rewards that don't match the user type
        const isLenderReward = reward.baseReward.isLenderReward && !isBorrower;
        const isBorrowerReward = !reward.baseReward.isLenderReward && isBorrower;

        if (!isLenderReward && !isBorrowerReward) {
            return sum;
        }

        return sum + reward.baseReward.pointsPerSecond;
    }, 0);

    // Convert to daily points
    return ledgerPoints * TIME.DAY;
}

export function calculateLendVaultDailyPoints(stakeReward: VaultReward | undefined): number | undefined {
    if (!stakeReward || !stakeReward.baseReward) return undefined;

    const ledgerPoints = stakeReward.baseReward.pointsPerSecond || 0;

    const totalPoints = ledgerPoints;
    const dailyPoints = totalPoints * TIME.DAY;

    return dailyPoints > 0 ? dailyPoints : undefined;
}

export function useGenesisRewardsPoints() {
    const pagination = useLendingPositionPagination();

    const { data: vaultPositions, isLoading: isLoadingVaultPositions } = useUserLendingVaultPositions(
        pagination.params
    );

    const vaultPositionsFromGenesisVaults = vaultPositions?.flatMap((pos) =>
        pos.vaultExpanded?.vaultMetadata?.isGenesisVault ? [pos.address] : []
    );

    const vaultPositionsFromGenesisVaultsSet = new Set(vaultPositionsFromGenesisVaults);

    const { data: portfolioRewardsForGenesisVaults, isLoading: isLoadingPortfolioRewards } = usePortfolioRewards({});

    const vaultStakesFromGenesisVaults = Object.fromEntries(
        Object.entries(portfolioRewardsForGenesisVaults?.vaultStakes ?? {}).filter(([address]) =>
            vaultPositionsFromGenesisVaultsSet.has(address)
        )
    );

    const genesisVaultPoints = Object.values(vaultStakesFromGenesisVaults).reduce((sum, stake) => {
        const isGenesisVault = stake.vaultMetadata?.tags?.includes("genesis") ?? false;
        return isGenesisVault ? sum + stake.baseReward.pointsPerSecond : sum;
    }, 0);

    return {
        totalPoints: genesisVaultPoints,
        isLoading: isLoadingPortfolioRewards || isLoadingVaultPositions
    };
}
